import { cn } from '@latitude/token-utils';
import type { ChangeEvent, ComponentRef, Ref } from 'react';
import React, { useEffect, useId, useState } from 'react';
import type { TextAreaProps } from 'types';

import { textAreaVariants } from './variants/textarea-variants';
import {
  contentWrapper,
  textAreaRootVariants,
} from './variants/textarea-wrapper-variants';

export const TextArea = ({
  textAreaSize,
  id,
  value,
  onChange,
  classNames = {},
  error,
  rawProps = {},
  ref,
  ...restProps
}: TextAreaProps & {
  ref?: Ref<HTMLTextAreaElement>;
}) => {
  const textAreaGenId = useId();
  const [textAreaValue, setTextAreaValue] = useState(value);

  useEffect(() => {
    setTextAreaValue(value);
  }, [value]);

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextAreaValue(e.target.value);
    onChange?.(e);
  };

  const textAreaId = id || textAreaGenId;

  const focusTextArea = () => {
    document.getElementById(textAreaId)?.focus();
  };

  const isDisabled = restProps.disabled;

  return (
    <div
      id="root"
      className={cn([
        textAreaRootVariants({ textAreaSize, hasError: error }),
        classNames.wrapper,
      ])}
      onClick={focusTextArea}
    >
      <div
        id="wrapper"
        className="relative flex w-full items-center justify-start"
      >
        <div
          id="contentWrapper"
          className={cn(contentWrapper({ textAreaSize }))}
        >
          <div className="flex flex-1 flex-wrap gap-1">
            <textarea
              ref={ref}
              id={textAreaId}
              value={textAreaValue}
              onChange={handleTextAreaChange}
              className={cn(
                textAreaVariants({
                  isDisabled,
                  textAreaSize,
                }),
                classNames.textArea,
              )}
              {...restProps}
              {...rawProps}
              readOnly={restProps.readOnly}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

TextArea.displayName = 'TextArea';

type DebouncedTextAreaProps = Omit<TextAreaProps, 'onChange'> & {
  onChange?: (value: TextAreaProps['value']) => void;
  debounce?: number;
};

export const DebouncedTextArea = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ref,
  ...props
}: DebouncedTextAreaProps & {
  ref?: Ref<ComponentRef<typeof TextArea>>;
}) => {
  const [value, setValue] = useState(initialValue);
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange?.(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);
  return (
    <TextArea
      ref={ref}
      value={value}
      onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
        setValue(event.target.value)
      }
      {...props}
    />
  );
};

DebouncedTextArea.displayName = 'DebouncedTextArea';

export { type TextAreaProps };
